import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { useMsal } from '@azure/msal-react';
import { apiConfig, apiTokenRequest } from '../authConfig';
import { getActiveImpersonation, clearActiveImpersonation } from '../utils/impersonationFetch';
import { formatDdMmYyyyTimeIst } from "../utils/formatDdMmYyyy";

const SESSION_ID_KEY = 'impersonationSessionId';

/**
 * Sticky amber banner shown while a Lead Advisor's impersonation session is active.
 *
 * Design spec: .claude/design/impersonation.md ("UI affordance" > "Header banner").
 * Mounted unconditionally near the app root (see App.jsx) — it renders nothing unless
 * sessionStorage has an active session, so it is always safe to mount.
 *
 * Renders purely off local sessionStorage; it does not itself grant or extend anything.
 * Every impersonated write is re-validated server-side against the ImpersonationSession row
 * (Commandment III/VI) — this banner is a display + "end it" affordance only.
 */
export default function ImpersonationBanner() {
    const { instance } = useMsal();
    const [rawId, setRawId] = useState(null);
    const [session, setSession] = useState(null);
    const [ending, setEnding] = useState(false);

    // Poll so the banner auto-hides once expiresAt passes, without requiring a page refresh.
    useEffect(() => {
        const tick = () => {
            let id = null;
            try {
                id = sessionStorage.getItem(SESSION_ID_KEY);
            } catch {
                id = null;
            }
            const active = getActiveImpersonation();
            if (id && !active) {
                // The id key is stale (session expired locally) — clear it so it doesn't linger
                // across reloads. The server independently enforces expiresAt regardless.
                clearActiveImpersonation();
                id = null;
            }
            setRawId(id);
            setSession(active);
        };
        tick();
        const timer = setInterval(tick, 15000);
        return () => clearInterval(timer);
    }, []);

    const handleEndNow = useCallback(async () => {
        if (!rawId) return;
        setEnding(true);
        try {
            const account = instance.getActiveAccount();
            const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
            await fetch(`${apiConfig.baseUrl}/api/v1/impersonation/${rawId}/end`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
            });
        } catch (err) {
            // Best-effort: even if the /end call fails (offline, already expired server-side),
            // still drop the local session so the LA isn't stuck "acting as" in this tab.
            console.error('Failed to end impersonation session cleanly:', err);
        } finally {
            clearActiveImpersonation();
            window.location.reload();
        }
    }, [rawId, instance]);

    // Only renders when a live session is present — see module doc above.
    if (!rawId || !session) return null;

    const effectiveRole = session.effectiveRole || 'Unknown role';
    const effectiveUserLabel = session.effectiveUserLabel || session.effectiveUserId || 'role-generic';
    const projectName = session.projectName || session.projectId || 'this project';
    const expiresAtLabel = session.expiresAt ? formatDdMmYyyyTimeIst(session.expiresAt) : '-';

    return (
        <Alert
            banner
            type="warning"
            showIcon={false}
            style={{ position: 'sticky', top: 0, zIndex: 1000, borderRadius: 0 }}
            message={
                <span>
                    {'⚡'} Acting as <strong>{effectiveRole}</strong> ({effectiveUserLabel}) on{' '}
                    <strong>{projectName}</strong> &middot; Ends at <strong>{expiresAtLabel}</strong>
                </span>
            }
            action={
                <Button size="small" danger loading={ending} onClick={handleEndNow}>
                    End now
                </Button>
            }
        />
    );
}
